import type { NewsLetterSubscriber, NewsLetterTemplateRecord } from '@ecowheels/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input } from '@/components/ui';
import { filterSubscribers } from '@/lib/newsletter-recipients';

/**
 * Picks the recipients for one template and sends to them.
 *
 * Replaces the previous flow, where recipients were ticked in the subscriber
 * table and the Küldés button stayed disabled until you had switched tabs and
 * back. Choosing recipients now happens at the moment of sending, next to the
 * template being sent, so the two decisions are never out of sight of one
 * another.
 */
export function SendNewsletterModal({
  template,
  subscribers,
  initialSelection,
  onCancel,
  onConfirm,
  isSending,
}: {
  template: NewsLetterTemplateRecord;
  subscribers: NewsLetterSubscriber[];
  /** Pre-ticked ids — used to open straight onto "everyone not yet asked". */
  initialSelection?: number[];
  onCancel: () => void;
  onConfirm: (recipients: NewsLetterSubscriber[]) => void;
  isSending?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(() => new Set(initialSelection ?? []));
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const visible = useMemo(() => filterSubscribers(subscribers, query), [subscribers, query]);
  const recipients = useMemo(
    () => subscribers.filter((s) => selected.has(s.id)),
    [subscribers, selected]
  );

  // Scoped to what is on screen: with a search active, "select all" ticking
  // people the user cannot see is how accidental sends happen. With no search
  // on screen every subscriber is visible, so the same action *is* "select
  // everyone" — the label says so rather than talking about "matches" when
  // nothing has been searched for.
  const isSearching = query.trim() !== '';
  const visibleAllSelected = visible.length > 0 && visible.every((s) => selected.has(s.id));
  const selectAllLabel = visibleAllSelected
    ? 'Kijelölés törlése'
    : isSearching
      ? 'Találatok kijelölése'
      : 'Összes kijelölése';

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const s of visible) {
        if (visibleAllSelected) next.delete(s.id);
        else next.add(s.id);
      }
      return next;
    });

  // Selections the search is hiding still receive mail, so say so rather than
  // let the count silently disagree with the visible ticks.
  const hiddenSelectedCount = recipients.length - visible.filter((s) => selected.has(s.id)).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${template.name} küldése`}
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-white/10 bg-ink"
      >
        <div className="border-b border-white/10 p-4">
          <h2 className="text-lg font-medium">{template.name} küldése</h2>
          <p className="mt-1 truncate text-sm text-white/50">{template.subject}</p>
          <div className="mt-3">
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Keresés név vagy email alapján…"
              aria-label="Keresés a feliratkozók között"
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-white/40">
            <span>
              {visible.length} {isSearching ? 'találat' : 'feliratkozó'} · {recipients.length}{' '}
              kijelölve
            </span>
            <Button variant="ghost" onClick={toggleVisible} disabled={visible.length === 0}>
              {selectAllLabel}
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {visible.length === 0 ? (
            <p className="p-4 text-center text-sm text-white/40">Nincs találat.</p>
          ) : (
            <ul>
              {visible.map((sub) => (
                <li key={sub.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selected.has(sub.id)}
                      onChange={() => toggle(sub.id)}
                      className="h-4 w-4 shrink-0 accent-brand"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{sub.name}</span>
                      <span className="block truncate text-xs text-white/50">{sub.email}</span>
                    </span>
                    {sub.velemenykeroSent && (
                      <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/60">
                        Véleménykérőt kapott
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/10 p-4">
          <p className="text-xs text-white/40">
            {hiddenSelectedCount > 0 && `${hiddenSelectedCount} kijelölt címzett a szűrőn kívül`}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={isSending}>
              Mégse
            </Button>
            <Button
              onClick={() => onConfirm(recipients)}
              disabled={recipients.length === 0 || isSending}
            >
              Küldés ({recipients.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
